import React, { useEffect, useState, useCallback } from 'react';
import {
    Table, Button, Form, Input, InputNumber, Select, message, Card, Row, Col, Tag, Modal, Popconfirm
} from 'antd';
import { ExperimentOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { labService } from '../../services';

const TEST_TYPES = ['blood', 'urine', 'imaging', 'biopsy', 'other'];

const LabCatalog = () => {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    const fetchCatalog = useCallback(async () => {
        setLoading(true);
        try {
            const res = await labService.getCatalog();
            setItems(res.data || []);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchCatalog();
    }, [fetchCatalog]);

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({ testType: 'blood', price: 0 });
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setEditing(record);
        form.setFieldsValue({
            testName: record.testName,
            testType: record.testType,
            price: record.price,
            referenceRange: record.referenceRange
        });
        setModalOpen(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);
            if (editing) {
                await labService.updateCatalogItem(editing._id, values);
                message.success(t('success'));
            } else {
                await labService.addCatalogItem(values);
                message.success(t('success'));
            }
            setModalOpen(false);
            fetchCatalog();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await labService.deleteCatalogItem(id);
            message.success(t('success'));
            fetchCatalog();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const columns = [
        { title: t('testName') || 'Test', dataIndex: 'testName', key: 'testName' },
        {
            title: t('testType') || 'Type',
            dataIndex: 'testType',
            key: 'testType',
            render: (v) => <Tag>{t(v) || v}</Tag>
        },
        {
            title: t('price') || 'Price',
            dataIndex: 'price',
            key: 'price',
            render: (v) => (v ? `${v} IQD` : '-')
        },
        {
            title: t('referenceRange') || 'Reference Range',
            dataIndex: 'referenceRange',
            key: 'referenceRange',
            render: (v) => v || '-'
        },
        {
            title: t('actions') || 'Actions',
            key: 'actions',
            render: (_, record) => (
                <>
                    <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(record)}>
                        {t('edit')}
                    </Button>
                    <Popconfirm
                        title={t('confirmDelete') || 'Delete this test?'}
                        onConfirm={() => handleDelete(record._id)}
                        okText={t('yes')}
                        cancelText={t('no')}
                    >
                        <Button type="link" danger icon={<DeleteOutlined />}>
                            {t('delete')}
                        </Button>
                    </Popconfirm>
                </>
            )
        }
    ];

    return (
        <div className="page-container">
            <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
                <Col>
                    <h1 style={{ margin: 0 }}>
                        <ExperimentOutlined /> {t('testCatalog') || 'Test Catalog'}
                    </h1>
                </Col>
                <Col>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        {t('addTest') || 'Add Test'}
                    </Button>
                </Col>
            </Row>

            <Card>
                <Table
                    columns={columns}
                    dataSource={items}
                    rowKey="_id"
                    loading={loading}
                    scroll={{ x: 700 }}
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            <Modal
                title={editing ? (t('editTest') || 'Edit Test') : (t('addTest') || 'Add Test')}
                open={modalOpen}
                onOk={handleSubmit}
                onCancel={() => setModalOpen(false)}
                confirmLoading={submitting}
                okText={t('save') || 'Save'}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="testName"
                        label={t('testName') || 'Test Name'}
                        rules={[{ required: true, message: t('required') }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item name="testType" label={t('testType') || 'Type'}>
                        <Select options={TEST_TYPES.map((v) => ({ value: v, label: t(v) || v }))} />
                    </Form.Item>
                    <Form.Item name="price" label={`${t('price') || 'Price'} (IQD)`}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="referenceRange" label={t('referenceRange') || 'Reference Range'}>
                        <Input placeholder="e.g. 70-110 mg/dL" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default LabCatalog;
